import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { LoginBrandingService } from '../../services/login-branding.service';
import { M31LoaderComponent } from '../m31-loader/m31-loader.component';
import { MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { getMsalScopesForActiveClientId, getSilentRedirectUri, setActiveMsalSettingsForEmail } from '../../utils/msal-config';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, M31LoaderComponent],
  template: `
    @if (branding.cargando()) {
      <app-m31-loader />
    } @else {
    <div
      class="login-raiz min-h-screen flex items-center justify-center bg-white dark:bg-black relative overflow-hidden transition-colors duration-500"
      [class.brand-color]="!!marca()?.color"
      [style.--brand]="marca()?.color"
      [style.--brand-ink]="marca()?.colorTexto"
      [style.--brand-fg-claro]="marca()?.colorTrazoClaro"
      [style.--brand-fg-oscuro]="marca()?.colorTrazoOscuro">
      @if (fondoVisible()) {
        <div class="brand-capa-fondo" [style.background-image]="fondoCss()"></div>
      }
      <button
        (click)="toggleTheme()"
        class="brand-tema absolute top-6 right-6 p-2 rounded-full text-gray-400 hover:text-ionet-600 dark:text-gray-500 dark:hover:text-ionet-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-all z-50 focus:outline-none"
        [title]="isDarkMode() ? 'Cambiar a Modo Claro' : 'Cambiar a Modo Oscuro'">
        @if (isDarkMode()) {
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
          </svg>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
          </svg>
        }
      </button>

      @if (!fondoVisible()) {
      <div class="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div class="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-ionet-200/40 dark:bg-ionet-900/10 rounded-full blur-[100px] mix-blend-multiply dark:mix-blend-normal animate-blob"></div>
        <div class="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-blue-100/40 dark:bg-blue-900/10 rounded-full blur-[100px] mix-blend-multiply dark:mix-blend-normal animate-blob animation-delay-2000"></div>
      </div>
      }

      <div class="relative bg-white/70 dark:bg-zinc-950/80 backdrop-blur-xl w-full max-w-md p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_20px_50px_rgb(0,0,0,0.5)] border border-white/60 dark:border-white/10 z-10 transition-all duration-500">
        @if (isRedirecting) {
          <div class="text-center py-8">
            <svg class="animate-spin mx-auto mb-4 h-10 w-10 text-ionet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-500 dark:text-gray-400 text-sm font-medium">Completando autenticacion con Microsoft...</p>
          </div>
        } @else {
          <div class="text-center mb-10">
            @if (marca()?.logoUrl; as logo) {
              <div class="brand-logo-placa inline-flex items-center justify-center mb-6">
                <img
                  [src]="logo"
                  alt="Logotipo de tu organización"
                  referrerpolicy="no-referrer"
                  class="block max-h-14 max-w-[210px] object-contain"
                  (error)="ocultarLogo()"
                />
              </div>
            } @else {
              <div class="brand-cuadro inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-tr from-ionet-600 to-ionet-500 text-white mb-6 shadow-lg shadow-ionet-500/30 transform transition-transform hover:scale-105 duration-300">
                <span class="text-2xl font-bold tracking-tighter">M31</span>
              </div>
            }
            <h2 class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight transition-colors">Bienvenido</h2>
            <!-- Es el lema de IONET: en la entrada de un cliente, con su propia marca, no va. -->
            @if (!marca()) {
              <p class="text-gray-500 dark:text-gray-400 mt-3 text-sm font-medium">Portal Inteligente IONET</p>
            }
          </div>

          @if (authErrorMessage) {
            <div class="mb-6 rounded-2xl border border-red-200/80 bg-red-50/90 dark:border-red-500/30 dark:bg-red-500/10 px-4 py-3 text-left text-sm text-red-800 dark:text-red-200 shadow-sm transition-all duration-300">
              <div class="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-300">
                  <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 7.5a.75.75 0 00-.75.75v4.5a.75.75 0 001.5 0v-4.5A.75.75 0 0012 7.5zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
                </svg>
                <div class="flex-1">
                  <p class="font-semibold">No pudimos iniciar sesion</p>
                  <p class="mt-1 leading-relaxed">{{ authErrorMessage }}</p>
                </div>
                <button
                  type="button"
                  (click)="clearAuthError()"
                  class="rounded-md p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
                  aria-label="Cerrar mensaje de error">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          }

          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="space-y-5">
            <div class="group">
              <label for="email" class="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 pl-1 transition-colors">Correo Corporativo</label>
              <div class="relative transition-all duration-300 transform group-focus-within:scale-[1.01]">
                <div class="brand-icono absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 group-focus-within:text-ionet-600 dark:group-focus-within:text-ionet-400 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                    <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
                    <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  formControlName="email"
                  class="block w-full pl-11 pr-4 py-3.5 border border-gray-200 dark:border-zinc-800 rounded-xl bg-gray-50 dark:bg-black text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-600 brand-campo focus:outline-none focus:ring-2 focus:ring-ionet-500/20 focus:border-ionet-500 dark:focus:border-ionet-400 transition-all duration-300"
                  placeholder="usuario@synaps.mx">
              </div>
            </div>

            <div class="group">
              <label for="password" class="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 pl-1 transition-colors">Contrasena</label>
              <div class="relative transition-all duration-300 transform group-focus-within:scale-[1.01]">
                <div class="brand-icono absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 group-focus-within:text-ionet-600 dark:group-focus-within:text-ionet-400 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                    <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd" />
                  </svg>
                </div>
                <input
                  type="password"
                  id="password"
                  formControlName="password"
                  class="block w-full pl-11 pr-4 py-3.5 border border-gray-200 dark:border-zinc-800 rounded-xl bg-gray-50 dark:bg-black text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-600 brand-campo focus:outline-none focus:ring-2 focus:ring-ionet-500/20 focus:border-ionet-500 dark:focus:border-ionet-400 transition-all duration-300"
                  placeholder="••••••••">
              </div>
            </div>

            <button
              type="submit"
              [disabled]="loginForm.invalid || isLoading"
              class="brand-btn w-full mt-2 flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-lg shadow-ionet-600/20 dark:shadow-ionet-900/30 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-ionet-600 to-ionet-500 hover:from-ionet-700 hover:to-ionet-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-black focus:ring-ionet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform active:scale-[0.98]">
              @if (isLoading) {
                <div class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Accediendo...</span>
                </div>
              } @else {
                Ingresar
              }
            </button>
          </form>

          <div class="mt-8 pt-6 border-t border-gray-100 dark:border-white/10 text-center transition-colors">
            <p class="text-xs font-medium text-gray-400 dark:text-gray-500">© 2026 SYNAPSE AI TECHNOLOGIES.</p>
            <p class="text-xs font-medium text-gray-400 dark:text-gray-500">Todos los derechos reservados.</p>
          </div>
        }
      </div>
    </div>
    }
  `,
  styles: [`
    /* Personalización por cliente. Cada campo que falte conserva el valor de siempre de M31:
       - Las reglas de COLOR cuelgan de .brand-color, que solo existe si el cliente trae color. Sin
         esa clase no hay --brand, y un var() sin definir deja el botón transparente.
       - El logotipo y el fondo tienen su propio condicional en la plantilla. */
    /* El fondo del cliente va en su propia capa para poder entrar con un fundido cuando termina de
       bajar, en vez de aparecer de golpe sobre un login ya pintado. */
    .brand-capa-fondo {
      position: absolute;
      inset: 0;
      z-index: 0;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      animation: fondo-entra 700ms ease both;
    }

    /* Un velo suave sobre el fondo: la tarjeta de vidrio queda legible con cualquier fotografía. */
    .brand-capa-fondo::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgb(0 0 0 / 0.32);
    }

    @keyframes fondo-entra { from { opacity: 0; } to { opacity: 1; } }

    @media (prefers-reduced-motion: reduce) {
      .brand-capa-fondo { animation: none; }
    }

    .brand-color .brand-btn {
      background-image: none;
      background-color: var(--brand);
      color: var(--brand-ink);
      box-shadow: 0 12px 26px -12px var(--brand), inset 0 0 0 1px color-mix(in srgb, var(--brand-ink) 22%, transparent);
    }
    .brand-color .brand-btn:hover:not(:disabled) { filter: brightness(1.08); }
    .brand-color .brand-btn:focus-visible { --tw-ring-color: var(--brand-fg); }
    /* El indicador de carga del botón es blanco fijo; sobre un color de marca claro no se vería. */
    .brand-color .brand-btn svg { color: var(--brand-ink); }

    /* El botón de tema flota sobre el fondo del cliente, que puede ser claro u oscuro: va en un
       disco del color de marca con el icono en el texto legible sobre ese color, no solo teñido. */
    .brand-color .brand-tema {
      background-color: var(--brand);
      color: var(--brand-ink);
      box-shadow: 0 6px 16px -6px var(--brand), inset 0 0 0 1px color-mix(in srgb, var(--brand-ink) 22%, transparent);
    }
    .brand-color .brand-tema:hover { filter: brightness(1.1); }

    /* Los trazos (borde de foco, candado) usan el color del cliente AJUSTADO para contrastar con la
       tarjeta: un amarillo o un blanco dejarían el foco invisible. Los rellenos usan el color tal cual. */
    .brand-color { --brand-fg: var(--brand-fg-claro); }
    :host-context(.dark) .brand-color { --brand-fg: var(--brand-fg-oscuro); }

    .brand-color .brand-campo:focus {
      border-color: var(--brand-fg);
      --tw-ring-color: color-mix(in srgb, var(--brand-fg) 28%, transparent);
    }
    .brand-color .group:focus-within .brand-icono { color: var(--brand-fg); }

    .brand-color .brand-cuadro {
      background-image: none;
      background-color: var(--brand);
      color: var(--brand-ink);
      box-shadow: 0 12px 26px -12px var(--brand), inset 0 0 0 1px color-mix(in srgb, var(--brand-ink) 22%, transparent);
    }

    /* Los logotipos suelen estar pensados para fondo claro: van sobre una placa blanca en ambos temas. */
    .brand-logo-placa {
      background: #fff;
      border-radius: 1.1rem;
      padding: 0.75rem 1.1rem;
      box-shadow: 0 10px 28px -14px rgb(0 0 0 / 0.35), 0 0 0 1px rgb(0 0 0 / 0.04);
    }
  `]
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly msalService = inject(MsalService, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  readonly branding = inject(LoginBrandingService);

  /** El logotipo del cliente falló al pintarse (la precarga ya lo descarta; esto es la red de seguridad). */
  private readonly logoFallido = signal(false);

  /** El fondo solo se pinta cuando la imagen ya bajó; puede ocurrir después de mostrar el login. */
  readonly fondoVisible = computed(() =>
    !!this.branding.branding()?.backgroundUrl && this.branding.fondoListo());

  /**
   * Personalización que realmente tiene algo que mostrar. Si el SIIC solo trajo un fondo que no
   * llegó a cargar, no hay marca visible y el login conserva su aspecto de siempre.
   */
  readonly marca = computed(() => {
    const vista = this.branding.branding();
    if (!vista) {
      return null;
    }

    const conLogo = this.logoFallido() ? { ...vista, logoUrl: null } : vista;
    return conLogo.logoUrl || conLogo.color || this.fondoVisible() ? conLogo : null;
  });

  /** Fondo como valor CSS; null cuando no hay, para que no se pinte nada. */
  readonly fondoCss = computed(() => {
    const url = this.branding.branding()?.backgroundUrl;
    return url && this.fondoVisible() ? `url("${url}")` : null;
  });

  ocultarLogo(): void {
    this.logoFallido.set(true);
  }

  readonly isDarkMode = this.themeService.isDarkMode;
  isLoading = false;
  isRedirecting = false;
  authErrorMessage: string | null = null;
  private errorTimerId: number | null = null;

  readonly loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required]
  });

  constructor() {
    this.loginForm.valueChanges.subscribe(() => {
      if (this.authErrorMessage) {
        this.clearAuthError();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Redirect callback is handled centrally in AppComponent to avoid race conditions.
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.clearAuthError();

    const { email, password } = this.loginForm.value;

    this.authService.login(email, password).subscribe({
      next: (success) => {
        if (success) {
          const requireMicrosoftLoginAfterSiic = this.authService.requiresMicrosoftLoginForCurrentUser();

          if (!requireMicrosoftLoginAfterSiic) {
            this.isLoading = false;
            this.router.navigate(['/chat']);
            return;
          }

          let targetSettings: { clientId: string; tenantId: string };
          try {
            // Select configured MSAL client and tenant based on the authenticated user's email domain.
            targetSettings = setActiveMsalSettingsForEmail(email);
          } catch (error) {
            this.isLoading = false;
            console.warn('[MSAL] Dominio no permitido para login:', error);
            this.showAuthError('El dominio del correo no esta habilitado para iniciar sesion.');
            return;
          }

          this.loginToMicrosoft(email, targetSettings);
        } else {
          this.isLoading = false;
          this.showAuthError(this.authService.lastLoginError() || 'Usuario o contrasena incorrectos. Verifique sus datos e intente nuevamente.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('[Auth Error] Fallo en WCF:', err);
        this.showAuthError('No pudimos validar tu acceso por un problema de conexion. Intenta de nuevo en unos segundos.');
      }
    });
  }

  private async loginToMicrosoft(email: string, targetSettings: { clientId: string; tenantId: string }): Promise<void> {
    try {
      const scopes = getMsalScopesForActiveClientId();
      const msalClient = await this.getMsalClientForTargetSettings(targetSettings.clientId, targetSettings.tenantId);
      await msalClient.handleRedirectPromise();
      const accounts = msalClient.getAllAccounts();

      if (accounts.length > 0) {
        const accountForLogin = accounts.find(a =>
          (a.username || '').toLowerCase() === email.toLowerCase()
        ) ?? accounts[0];

        try {
          const silentResult = await msalClient.acquireTokenSilent({
            scopes,
            account: accountForLogin,
            redirectUri: getSilentRedirectUri()
          });

          msalClient.setActiveAccount(silentResult.account);
          this.authService.setMicrosoftToken(silentResult.accessToken);
          this.isLoading = false;
          this.router.navigate(['/chat']);
          return;
        } catch (silentError) {
          // No se pudo renovar en silencio (sesión caducada, cookies bloqueadas, etc.).
          // Se continúa al login interactivo: antes el flujo se detenía aquí y la pantalla
          // quedaba cargando para siempre.
          console.warn('[MSAL] Renovación silenciosa fallida; se continúa con el login interactivo.', silentError);
        }
      }

      // Único camino de salida cuando no hubo token silencioso.
      await msalClient.loginRedirect({
        scopes,
        loginHint: email
      });
    } catch (error) {
      console.error('[MSAL Error]:', error);
      this.isLoading = false;
      this.showAuthError('No fue posible obtener el token de Microsoft para los servicios del chat.');
    }
  }

  private async getMsalClientForTargetSettings(targetClientId: string, targetTenantId: string): Promise<IPublicClientApplication> {
    const targetAuthority = `https://login.microsoftonline.com/${targetTenantId}`;

    if (this.msalService) {
      const activeInstance = this.msalService.instance;
      const activeClientId = activeInstance.getConfiguration().auth.clientId;
      const activeAuthority = (activeInstance.getConfiguration().auth.authority ?? '').toLowerCase();

      if (activeClientId === targetClientId && activeAuthority === targetAuthority.toLowerCase()) {
        return activeInstance;
      }
    }

    const origin = window.location.origin;
    const tempClient = new PublicClientApplication({
      auth: {
        clientId: targetClientId,
        authority: targetAuthority,
        redirectUri: `${origin}/login`,
        postLogoutRedirectUri: `${origin}/login`
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage
      },
      system: {
        allowRedirectInIframe: true
      }
    });

    await tempClient.initialize();
    return tempClient;
  }

  private showAuthError(message: string): void {
    this.authErrorMessage = message;

    if (this.errorTimerId !== null) {
      window.clearTimeout(this.errorTimerId);
    }

    this.errorTimerId = window.setTimeout(() => {
      this.clearAuthError();
    }, 7000);

    this.refreshView();
  }

  clearAuthError(): void {
    this.authErrorMessage = null;
    if (this.errorTimerId !== null) {
      window.clearTimeout(this.errorTimerId);
      this.errorTimerId = null;
    }

    this.refreshView();
  }

  private refreshView(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.clearAuthError();
  }
}
